import * as React from "react";
import { useEffect, useState } from "react";
import { Button, ButtonColor, ButtonSize, ButtonVariant, Checkbox, Spinner, Text, TextVariant } from "../../ui";
import { FullScreenDialog } from "../../FullScreenDialog";
import { PartOrder, PartOrderItemV2 } from "../../../generated/nest-graphql";
import { isAwaitingPickup } from "../partsStatus.utils";

/**
 * "All or some?", then the list if it's some. Asking up front is what makes a partial
 * pickup a first-class answer rather than something the technician has to express.
 */
export const ConfirmPickupDialog: React.FC<{
  order: PartOrder | null;
  open: boolean;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (itemIds: string[]) => void;
}> = ({ order, open, submitting, onClose, onConfirm }) => {
  const [mode, setMode] = useState<"ask" | "some">("ask");
  const [selected, setSelected] = useState<string[]>([]);

  // Reset whenever a different order opens the dialog, otherwise the previous
  // order's selection carries over into this one.
  useEffect(() => {
    if (open) {
      setMode("ask");
      setSelected([]);
    }
  }, [open, order?.id]);

  const pending = ((order?.items ?? []).filter(Boolean) as PartOrderItemV2[]).filter(isAwaitingPickup);

  const toggle = (id: string) =>
    setSelected((current) => (current.includes(id) ? current.filter((it) => it !== id) : [...current, id]));

  return (
    <FullScreenDialog title="Confirm pickup" open={open} onClose={submitting ? undefined : onClose}>
      <div className="p-4 flex flex-col gap-4">
        <div>
          <Text variant={TextVariant.BODY2} className="text-gray-400">
            Order
          </Text>
          <Text variant={TextVariant.H6}>{order?.orderNumber || "No order number"}</Text>
          <Text variant={TextVariant.BODY2} className="text-gray-400">
            {order?.store?.name}
          </Text>
        </div>

        {mode === "ask" ? (
          <>
            <Text variant={TextVariant.H6} className="mt-4">
              Are you picking up all {pending.length} {pending.length === 1 ? "part" : "parts"}?
            </Text>
            <div className="flex flex-col gap-3 mt-2">
              <Button
                variant={ButtonVariant.CONTAINED}
                color={ButtonColor.PRIMARY}
                size={ButtonSize.LARGE}
                disabled={submitting || pending.length === 0}
                onClick={() => onConfirm(pending.map((item) => item.id))}
              >
                {submitting ? <Spinner size={22} /> : "Yes — all of them"}
              </Button>
              <Button
                variant={ButtonVariant.OUTLINED}
                size={ButtonSize.LARGE}
                disabled={submitting}
                onClick={() => setMode("some")}
              >
                No — only some
              </Button>
            </div>
          </>
        ) : (
          <>
            <Text variant={TextVariant.BODY1} className="mt-2">
              Select what you have. Anything you leave unticked stays marked as still at the store.
            </Text>
            <div className="flex flex-col divide-y divide-gray-700 border border-gray-700 rounded">
              {pending.map((item) => (
                <label key={item.id} className="flex items-center gap-2 p-3 cursor-pointer">
                  <Checkbox
                    checked={selected.includes(item.id)}
                    onChange={() => toggle(item.id)}
                    disabled={submitting}
                  />
                  <span className="flex-grow">
                    <span className="block font-semibold">{item.name || "Unnamed part"}</span>
                    <span className="block text-sm text-gray-400">
                      {item.units ?? 1}&times; {item.partNumber || "no part number"}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            <Button
              variant={ButtonVariant.CONTAINED}
              color={ButtonColor.PRIMARY}
              size={ButtonSize.LARGE}
              disabled={submitting || selected.length === 0}
              onClick={() => onConfirm(selected)}
            >
              {submitting ? (
                <Spinner size={22} />
              ) : (
                `Confirm ${selected.length} ${selected.length === 1 ? "part" : "parts"}`
              )}
            </Button>
          </>
        )}
      </div>
    </FullScreenDialog>
  );
};
